import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import type { CoverLetter, OutputLanguage } from '../types';

/**
 * Rendu de la lettre — spécification de mise en page de la section 6, transposée en CSS.
 *
 * A4, marges 2 cm, Times New Roman 11 pt, corps justifié, aucun paragraphe vide (uniquement des
 * marges), contrôle des veuves et orphelines. Le PDF est produit SUR L'APPAREIL par expo-print,
 * comme l'export de CV : aucun écrivain .docx n'existe dans le projet, et la mise en page est
 * déterministe dans les deux cas.
 *
 * L'interligne ne se mesure pas : il est fourni par le serveur, dérivé du nombre de mots recompté
 * (~300 mots → 1,5 ; ~330 → 1,3 ; au-delà → 1,15). C'est ce qui remplace la boucle de rendu
 * headless du document, qu'on n'a pas.
 */

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Typographie française, appliquée au RENDU et non par le modèle (section 6) :
 * espace insécable dans les milliers, à l'intérieur des guillemets, et avant les deux-points et
 * points-virgules. Sans quoi « 130 000 » se coupe en fin de ligne.
 */
export function typographieFr(texte: string): string {
  return texte
    .replace(/(\d) (?=\d{3}\b)/g, '$1 ')
    .replace(/« /g, '« ')
    .replace(/ »/g, ' »')
    .replace(/ ([;:])/g, ' $1');
}

/**
 * La typographie française n'est appliquée QU'AUX LETTRES FRANÇAISES.
 *
 * Ses règles sont INERTES sur un texte anglais — pas d'espace avant le point-virgule, pas de
 * guillemets français, virgules pour les milliers — mais c'est une coïncidence, pas un contrat. Une
 * lettre anglaise citant « 130 000 » à la française y gagnerait une espace insécable qui n'a rien à
 * y faire. La langue vient du DOCUMENT (cover_letters.output_language), jamais de l'interface.
 */
const prepare = (texte: string, language: OutputLanguage): string =>
  escapeHtml(language === 'fr' ? typographieFr(texte) : texte);

export function buildLetterHtml(letter: CoverLetter, outputLanguage: OutputLanguage = 'fr'): string {
  const { candidat, destinataire, lettre } = letter;
  const fmt = (texte: string) => prepare(texte, outputLanguage);

  const candidatLignes = [
    candidat.nom_complet,
    candidat.ville_ou_mobilite,
    candidat.telephone ? `Tel: ${candidat.telephone}` : '',
    candidat.email ? `Email: ${candidat.email}` : '',
  ].filter((l) => l.trim());

  const destinataireLignes = [
    destinataire.organisation,
    destinataire.service_ou_fondation,
    [destinataire.nom, destinataire.fonction].filter((v) => v.trim()).join(' — '),
    destinataire.ville,
  ].filter((l) => l.trim());

  const ligne = (l: string) => `<p class="l">${fmt(l)}</p>`;

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8" />
<style>
  @page { size: A4; margin: 2cm; }
  * { box-sizing: border-box; }
  body {
    font-family: 'Times New Roman', Times, serif;
    font-size: 11pt;
    color: #000;
    margin: 0;
    /* Veuves et orphelines : jamais une ligne seule en haut ou en bas de page. */
    orphans: 2;
    widows: 2;
  }
  p { margin: 0; }
  .l { line-height: 1.25; }
  .bloc-candidat { text-align: left; margin-bottom: 12pt; }
  .bloc-destinataire { text-align: right; margin-bottom: 12pt; }
  .date { text-align: right; margin-bottom: 24pt; }
  .objet { text-align: left; margin-bottom: 24pt; }
  .appel { text-align: left; margin-bottom: 12pt; }
  /* Interligne du corps : fourni par le serveur, seul élément variable de la mise en page. */
  .corps p {
    text-align: justify;
    line-height: ${letter.interligne};
    margin-bottom: 6pt;
  }
  .politesse { text-align: left; margin-top: 6pt; margin-bottom: 15pt; }
  .signature { text-align: left; }
</style>
</head>
<body>
  <div class="bloc-candidat">${candidatLignes.map(ligne).join('')}</div>
  <div class="bloc-destinataire">${destinataireLignes.map(ligne).join('')}</div>
  <p class="date">${fmt(letter.lieu_date)}</p>
  <p class="objet">Objet : ${fmt(letter.objet)}</p>
  <p class="appel">${fmt(lettre.formule_appel)}</p>
  <div class="corps">${lettre.paragraphes.map((p) => `<p>${fmt(p)}</p>`).join('')}</div>
  <p class="politesse">${fmt(lettre.formule_politesse)}</p>
  <p class="signature">${fmt(lettre.signature)}</p>
</body>
</html>
`;
}

// Génère le PDF localement (étape gratuite, AVANT le gating d'export).
export async function generateLetterPdf(
  letter: CoverLetter,
  outputLanguage: OutputLanguage = 'fr',
): Promise<string> {
  const { uri } = await Print.printToFileAsync({ html: buildLetterHtml(letter, outputLanguage) });
  return uri;
}

export async function shareLetterPdf(uri: string): Promise<void> {
  const available = await Sharing.isAvailableAsync();
  if (!available) throw new Error('Le partage n’est pas disponible sur cet appareil.');
  await Sharing.shareAsync(uri, { mimeType: 'application/pdf', UTI: 'com.adobe.pdf' });
}
